<template>
	<span class="save-indicator" :class="`save-${state}`" aria-live="polite">
		<template v-if="state === 'saving'">
			<span class="spinner-border spinner-border-sm me-1" role="status"></span>
			Saving…
		</template>
		<template v-else-if="state === 'saved'">
			<i class="bi bi-check-lg me-1"></i>Saved
		</template>
		<!-- The session ended. The edits are held, not lost, so the indicator
		     says both things: nothing was saved, and there is a way back to
		     saving — sign in again, or retry once the session is back. -->
		<template v-else-if="state === 'signed-out'">
			<i class="bi bi-person-slash me-1"></i>Not saved — signed out
			<span v-if="pending > 0" class="text-nowrap">
				({{ pending }} waiting)
			</span>
			<RouterLink class="indicator-action" :to="signinTarget">
				Sign in again
			</RouterLink>
			<button
				type="button"
				class="indicator-action indicator-button"
				@click="emit('retry')"
			>
				Retry
			</button>
		</template>
		<template v-else-if="state === 'failed'">
			<i class="bi bi-exclamation-triangle me-1"></i>Not saved
		</template>
	</span>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useRoute } from "vue-router";

import type { SaveState } from "@/lib/autoSave";

withDefaults(defineProps<{ state: SaveState; pending?: number }>(), {
	pending: 0,
});
const emit = defineEmits<{ (e: "retry"): void }>();

// Signing in again should land back on the surface whose edits are held.
const route = useRoute();
const signinTarget = computed(() => ({
	path: "/signin",
	query: { next: route.fullPath },
}));
</script>

<style scoped>
.save-indicator {
	font-size: 0.8rem;
	color: #6c757d;
	white-space: nowrap;
}

.save-saved {
	color: #198754;
}

.save-failed,
.save-signed-out {
	color: #dc3545;
}

.indicator-action {
	margin-left: 0.5rem;
	font-size: 0.8rem;
}

.indicator-button {
	padding: 0;
	border: 0;
	background: none;
	text-decoration: underline;
	color: inherit;
}
</style>
